import type { ReactNode } from "react";
import { MouseEventHandler, ReactElement } from "react";
import { JSX } from "react/jsx-runtime";

import { Button, FormControl, Grid, Paper, Select, styled } from "@mui/material";
import { styled as styledMUI } from "@mui/material/styles";
import { SearchTextBox } from "../SearchTextBox";
import IconInfo from "../common/IconInfo";

const LeftAlignDiv = styled("div")`
  text-align: left;
`;

const FormGroupItem = styledMUI(Grid, {
  shouldForwardProp: (prop) => prop !== "wide",
})<{ wide?: boolean }>(({ wide }) => ({
  minWidth: wide ? "200px" : "100px",
}));

const FilterCard = styled(Paper)(({ theme }) => ({
  padding: "20px 20px",
  boxShadow: "none",
  border: `1px solid ${theme.palette.surfaceLight30.main}`,
}));

const FilterLabel = styledMUI("label")(({ theme }) => ({
  display: "flex",
  color: theme.palette.text50.main,
  fontWeight: "normal",
  fontSize: "12px",
  userSelect: "none",
  alignItems: "center",
}));

const ExplainTitle = styled("span")`
  text-decoration: underline;
  font-weight: bold;
`;

const FilterForm = ({ children }: { children: ReactNode }) => {
  return (
    <FilterCard>
      <Grid
        container
        direction="row"
        spacing={2}
        sx={{ justifyContent: "flex-start", alignItems: "center", width: "100%" }}
      >
        {children}
      </Grid>
    </FilterCard>
  );
};

const SelectStyled = styledMUI(Select<string | null>)({
  height: "32px",
  textTransform: "none",
});

const FormControlSelect = styledMUI(FormControl)({
  m: 1,
  width: 170,
  margin: 0,
});

type FilterSearchTextProps = {
  search: string;
  onChange: (searchTerm: string) => void;
  explain: JSX.Element;
};

const FilterSearchText = ({ search, onChange, explain }: FilterSearchTextProps) => (
  <>
    <FilterLabel className="form-control-title">Search:{explain}</FilterLabel>
    <SearchTextBox className="form-control" value={search} onChange={onChange} />
  </>
);

type ClearSearchButtonProps = { onClear: () => void; text?: string };

const ClearSearchButton = ({ onClear, text = "Clear" }: ClearSearchButtonProps) => (
  <Button type="button" onClick={onClear} variant="text" data-cy="clear-filters">
    {text}
  </Button>
);

const CancelLinkButton = ({ onCancel }: { onCancel?: MouseEventHandler<HTMLButtonElement> }) => (
  <FormGroupItem>
    <Button type="button" name="cancelLink" onClick={onCancel} variant="text" color="secondary">
      Cancel
    </Button>
  </FormGroupItem>
);

const InfoIconPurpose = ({ children }: { children: ReactNode }) => {
  const text = (
    <LeftAlignDiv>
      <span>{children}</span>
    </LeftAlignDiv>
  );
  return <IconInfo text={text} />;
};

type FilterValue = {
  value: string;
  label: string;
};

interface IFilterSelect {
  id: string;
  title: string;
  selected?: string | null;
  values: FilterValue[];
  onChange: (value: string | null) => void;
  infoText?: ReactElement;
  dataCy: string;
}

const FilterSelect = ({ id, title, selected, values, onChange, infoText, dataCy }: IFilterSelect) => {
  return (
    <>
      <FilterLabel className="form-control-title">
        {title}:{infoText ? <InfoIconPurpose>{infoText}</InfoIconPurpose> : null}
      </FilterLabel>

      <FormControlSelect>
        <SelectStyled
          id={id}
          className="form-control"
          value={selected}
          onChange={(e) => onChange(e.target.value)}
          data-cy={dataCy}
          variant="outlined"
          displayEmpty
          native={true}
        >
          {values.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </SelectStyled>
      </FormControlSelect>
    </>
  );
};

const VIEW_BY_DOWNLOAD_DATE_TYPE = [
  { value: "downloadDate", label: "Downloaded Date" },
  { value: "reportDate", label: "Report Date" },
];
const DOWNLOAD_DATE_TYPE_EXPLAIN = (
  <div>
    Downloaded Date: Date Downloaded started or finished on.
    <br />
    Report Date: Date of the downloaded data (where appropriate).
  </div>
);

interface IFilterByDownloadDateTypes {
  dateRangeType: string;
  onChange: (value: string | null) => void;
}
const FilterByDownloadDateTypes = ({ dateRangeType, onChange }: IFilterByDownloadDateTypes) => (
  <FilterSelect
    id="filter-by-download"
    title={"Group By"}
    values={VIEW_BY_DOWNLOAD_DATE_TYPE}
    infoText={DOWNLOAD_DATE_TYPE_EXPLAIN}
    selected={dateRangeType}
    onChange={onChange}
    dataCy=""
  />
);

const VIEW_BY_UPLOAD_DATE_TYPE = [
  { value: "uploadDate", label: "Uploaded Date" },
  { value: "reportDate", label: "Report Date" },
];
const UPLOAD_DATE_TYPE_EXPLAIN = (
  <div>
    Uploaded Date: Date Upload started or finished on.
    <br />
    Report Date: Date of the uploaded data (where appropriate).
  </div>
);

type FilterByUploadDateTypesProps = {
  id: string;
  dateRangeType?: string | null;
  onChange: (value: string | null) => void;
};

const FilterByUploadDateTypes = ({ id, dateRangeType, onChange }: FilterByUploadDateTypesProps) => (
  <FilterSelect
    id={id}
    title={"Group By"}
    values={VIEW_BY_UPLOAD_DATE_TYPE}
    infoText={UPLOAD_DATE_TYPE_EXPLAIN}
    selected={dateRangeType}
    onChange={onChange}
    dataCy=""
  />
);

export {
  CancelLinkButton,
  ClearSearchButton,
  ExplainTitle,
  FilterByDownloadDateTypes,
  FilterByUploadDateTypes,
  FilterForm,
  FilterLabel,
  FilterSearchText,
  FilterSelect,
  FormGroupItem,
  LeftAlignDiv,
};
